import { useCallback, useRef, useState } from 'react';
import { recordVoiceHistoryEntry } from '@/api/assistantVoice/assistantVoiceApi';
import type { VoiceHistoryRole } from '@/api/assistantVoice/assistantVoiceApi.types';
import { retryOnceAfterTransientError } from './retryOnceAfterTransientError';

interface VoiceHistoryLog {
  conversationId: string | null;
  pendingWrites: Promise<void>;
}

export interface VoiceHistory {
  conversationId: string | null;
  startNewConversation: () => void;
  recordLine: (role: VoiceHistoryRole, text: string) => void;
}

function emptyLog(): VoiceHistoryLog {
  return { conversationId: null, pendingWrites: Promise.resolve() };
}

export function useVoiceHistory(): VoiceHistory {
  const logRef = useRef<VoiceHistoryLog>(emptyLog());
  const [conversationId, setConversationId] = useState<string | null>(null);

  const startNewConversation = useCallback(function startNewConversation() {
    logRef.current = emptyLog();
    setConversationId(null);
  }, []);

  const recordLine = useCallback(function recordLine(role: VoiceHistoryRole, text: string) {
    const trimmedText = text.trim();
    if (trimmedText === '') {
      return;
    }
    const log = logRef.current;
    log.pendingWrites = log.pendingWrites.then(async function writeLine() {
      try {
        const savedConversationId = await retryOnceAfterTransientError(function saveLine() {
          return recordVoiceHistoryEntry(log.conversationId, role, trimmedText);
        });
        log.conversationId = savedConversationId;
        if (logRef.current === log) {
          setConversationId(savedConversationId);
        }
      } catch {
        return;
      }
    });
  }, []);

  return { conversationId, startNewConversation, recordLine };
}
